// -------------------------------
// Utility Helpers
// -------------------------------
const $ = (selector, ctx = document) => ctx.querySelector(selector);
const $$ = (selector, ctx = document) => [...ctx.querySelectorAll(selector)];

function updateHiddenInput(form, name, value) {
  const input = form.querySelector(`input[name='${name}']`);
  if (input && value) input.value = value;
}

function renderPackOptions(tabId) {
  if (tabId === "oat-melts") {
    return `<button class="active">24-pack</button>`;
  }
  return ["6-pack", "12-pack", "18-pack"]
    .map((label, i) => `<button ${i === 0 ? 'class="active"' : ""}>${label}</button>`)
    .join("");
}

function animateChildren(children, show = true) {
  children.forEach((child, index) => {
    child.style.transition = `opacity 0.4s ease ${index * 0.1}s, transform 0.4s ease ${index * 0.1}s`;
    child.style.opacity = show ? 1 : 0;
    child.style.transform = show ? "translateY(0)" : "translateY(-20px)";
  });
}

// -------------------------------
// Event Bus
// -------------------------------
class EventBus {
  constructor() {
    this.events = {};
  }

  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
  }

  publish(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(cb => cb(data));
  }
}

const eventBus = new EventBus();

// -------------------------------
// Renderer Base
// -------------------------------
class Renderer {
  render() {
    throw new Error("Render method must be implemented");
  }
}

class CartItemRenderer extends Renderer {
  constructor(item) {
    super();
    this.item = item;
  }

  render() {
    const template = $("#item-card-template");
    const clone = template.content.cloneNode(true);

    $(".cart-item-title", clone).textContent = this.item.product_title;
    $(".cart-item-variant", clone).textContent = this.item.variant_title || "";
    $(".item-cart-price", clone).textContent = Shopify.formatMoney(this.item.line_price);
    $(".cart-item-img", clone).src = this.item.image || "";

    const removeBtn = $(".cart-item-remove", clone);
    if (removeBtn) {
      removeBtn.dataset.key = this.item.key;
      removeBtn.addEventListener("click", async (e) => {
        e.preventDefault();

        const originalHTML = removeBtn.innerHTML;
        removeBtn.innerHTML = `<span class="spinner"></span>`;
        removeBtn.disabled = true;

        try {
          await Cart.removeItem(this.item.key);
        } catch (err) {
          console.error("Error removing item:", err);
          alert("Unable to remove item. Please try again.");
          removeBtn.innerHTML = originalHTML;
          removeBtn.disabled = false;
        }
      });
    }

    return clone;
  }
}

// -------------------------------
// Cart + MiniCart
// -------------------------------
class Cart {
  static async fetchCart() {
    const res = await fetch("/cart.js");
    if (!res.ok) throw new Error("Failed to fetch cart");
    return await res.json();
  }

  static async removeItem(key) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: key, quantity: 0 }),
    });
    if (!res.ok) throw new Error("Failed to remove item");

    const cart = await res.json();
    eventBus.publish("cart:updated", cart);
    return cart;
  }

  static async addItem(formData) {
    MiniCart.showSkeleton(); 
    const res = await fetch("/cart/add.js", {
      method: "POST",
      body: formData,
      headers: { "X-Requested-With": "XMLHttpRequest" }
    });
    if (!res.ok) throw new Error("Failed to add product");
    const added = await res.json();
    const cart = await Cart.fetchCart();
    eventBus.publish("cart:updated", cart);
    return added;
  }

  static async clear() {
    MiniCart.showSkeleton(); 
    await fetch("/cart/clear.js", { method: "POST" });
    const cart = await Cart.fetchCart();
    eventBus.publish("cart:updated", cart);
  }
}

class MiniCart {
  static container = $(".mini-cart-items");

  static showSkeleton(count = 2) {
    if (!MiniCart.container) return;
    MiniCart.container.innerHTML = "";
    for (let i = 0; i < count; i++) {
      MiniCart.container.innerHTML += `
        <div class="mini-cart-skeleton">
          <div class="skeleton img"></div>
          <div class="text">
            <div class="skeleton line"></div>
            <div class="skeleton line" style="width:50%"></div>
          </div>
        </div>
      `;
    }
  }

  static render(cart) {
    if (!MiniCart.container) return;
    MiniCart.container.innerHTML = "";
    cart.items.forEach(item => {
      const renderer = new CartItemRenderer(item);
      MiniCart.container.appendChild(renderer.render());
    });
  }
}

// -------------------------------
// Pricing Summary
// -------------------------------
class PricingSummary {
  static render(cart) {
    if (!cart) return;

    const DISCOUNT = 0.20;
    const subscriptionTotal = cart.items.reduce((sum, item) => sum + item.line_price, 0);
    const oneTimeTotal = Math.round(subscriptionTotal / (1 - DISCOUNT));
    const savings = oneTimeTotal - subscriptionTotal;

    const otPriceEl = $(".ot-price");
    const subPriceEl = $(".sub-price");
    const saveAmountEl = $(".save-amount");
    const checkoutBtn = $(".checkout-btn button");
    const itemCountEl = $(".item-count");

    if (otPriceEl) otPriceEl.textContent = Shopify.formatMoney(oneTimeTotal);
    if (subPriceEl) subPriceEl.textContent = Shopify.formatMoney(subscriptionTotal);
    if (saveAmountEl) saveAmountEl.textContent = Shopify.formatMoney(savings);

    if (checkoutBtn) {
      if (cart.item_count === 0) {
        checkoutBtn.setAttribute("disabled", "true");
        checkoutBtn.textContent = "Checkout";
        checkoutBtn.style.opacity = "0.5";
        checkoutBtn.style.cursor = "not-allowed";
      } else {
        checkoutBtn.removeAttribute("disabled");
        checkoutBtn.textContent = `Checkout: ${Shopify.formatMoney(subscriptionTotal)}`;
        checkoutBtn.style.opacity = "1";
        checkoutBtn.style.cursor = "pointer";
      }
    }

    if (itemCountEl) {
      itemCountEl.textContent = cart.item_count;
    }
  }
}

// -------------------------------
// Form Handler (Add to Cart)
// -------------------------------
class AddToCartFormHandler {
  constructor(formClass) {
    this.formClass = formClass;
    document.addEventListener("submit", (e) => this.handleSubmit(e));
  }

  async handleSubmit(e) {
    if (!e.target.classList.contains(this.formClass)) return;
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    const submitBtn = $("button[type='submit']", form);
    const originalText = submitBtn ? submitBtn.textContent : "";

    try {
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = `<span class="spinner"></span>`;
      }
      await Cart.addItem(formData);

    } catch (err) {
      console.error("Error adding to cart:", err);
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
    }
  }

}

class ProductTabHandler {
  constructor(
    tabSelector,
    productSelector,
    activeClass = "filtered",
    titleSelector = ".col-title",
    priceSelector = ".pc-price",
    dropdownSelector = ".product-filter-dropdown"
  ) {
    this.tabs = $$(tabSelector);
    this.products = $$(productSelector);
    this.activeClass = activeClass;
    this.titleEl = $(titleSelector);
    this.priceEl = $$(priceSelector);
    this.dropdown = $(dropdownSelector);
    this.variantHandler = null;

    this.productPrices = {
      "smoothie-melts": 29.99,
      "fruit-bites": 29.99,
      "oat-melts": 39.99
    };

    this.tabs.forEach(tab =>
      tab.addEventListener("click", () => this.showTab(tab.id, tab.textContent.trim()))
    );
    if (this.dropdown) {
      this.dropdown.addEventListener("change", (e) => {
        const selectedId = e.target.value;
        const selectedLabel = e.target.options[e.target.selectedIndex].textContent.trim();
        this.showTab(selectedId, selectedLabel);
      });
    }
    if (this.tabs.length) {
      this.showTab(this.tabs[0].id, this.tabs[0].textContent.trim());
    }
  }

  showTab(tabId, tabLabel) {
    this.tabs.forEach(t => t.classList.toggle(this.activeClass, t.id === tabId));
    this.products.forEach(p => {
      if (p.dataset.tab === tabId) {
        p.style.display = "";
        animateChildren(Array.from(p.children), true);
      } else {
        animateChildren(Array.from(p.children), false);
        setTimeout(() => p.style.display = "none", 200);
      }
    });
    if (this.titleEl) this.titleEl.textContent = tabLabel;
    if (this.priceEl && this.productPrices[tabId] !== undefined) {
      this.priceEl.forEach(el => el.textContent = Shopify.formatMoney(this.productPrices[tabId] * 100));
    }
    if (this.dropdown) this.dropdown.value = tabId;

    const packContainer = $(".pack-sizes");
    if (packContainer) {
      packContainer.innerHTML = renderPackOptions(tabId);
      if (this.variantHandler) {
        this.variantHandler = null;
      }
      this.variantHandler = new VariantAndPlanHandler(".pack-sizes button", ".custom-select", ".product-card-wrapper");
    }
  }
}

class VariantAndPlanHandler {
  constructor(packSelector, planSelector, productSelector) {
    this.packButtons = $$(packSelector);
    this.planDropdown = $(planSelector);
    this.productCards = $$(productSelector);
    this.state = { selectedPack: null, selectedPlan: null };
    this.packPrices = {
      "smoothie-melts": { "6-pack": 31.99, "12-pack": 54.39, "18-pack": 78.39 },
      "fruit-bites": { "6-pack": 31.99, "12-pack": 54.99, "18-pack": 78.39 }
    };
    this.initialize();
  }
  initialize() {
    const activeBtn = $(".pack-sizes button.active");
    this.state.selectedPack = activeBtn ? activeBtn.textContent.trim() : null;
    this.state.selectedPlan = this.planDropdown ? this.planDropdown.value : null;
    this.packButtons.forEach(btn => btn.addEventListener("click", () => this.handlePackClick(btn)));
    if (this.planDropdown) this.planDropdown.addEventListener("change", e => this.handlePlanChange(e));
    this.updateAllProductForms();
    this.updatePcPrice();
  }
  handlePackClick(btn) {
    this.packButtons.forEach(b => b.classList.remove("active"));
    btn.classList.add("active");
    this.state.selectedPack = btn.textContent.trim();
    this.updateAllProductForms();
    this.updatePcPrice();
  }
  handlePlanChange(e) {
    this.state.selectedPlan = e.target.value;
    this.updateAllProductForms();
  }
  updateAllProductForms() {
    this.productCards.forEach(card => {
      const variants = JSON.parse(card.dataset.variants || "{}");
      const sellingPlans = JSON.parse(card.dataset.sellingPlans || "{}");
      const form = $("form", card);
      if (!form) return;
      const packKey = this.getPackKey(this.state.selectedPack, variants);
      updateHiddenInput(form, "id", variants[packKey]);
      updateHiddenInput(form, "selling_plan", sellingPlans[this.state.selectedPlan]);
    });
    if (this.state.selectedPack) {
      $$(".product-badge").forEach(badge => { badge.textContent = this.state.selectedPack; });
    }
  }
  updatePcPrice() {
    const activeTab = $(".product-filter button.filtered");
    if (!activeTab) return;
    const tabId = activeTab.id;
    if (tabId === "oat-melts") {
      $$(".pc-price").forEach(el => el.textContent = Shopify.formatMoney(39.99 * 100));
      return;
    }
    if (!this.packPrices[tabId]) return;
    const newPrice = this.packPrices[tabId][this.state.selectedPack];
    if (!newPrice) return;
    $$(".pc-price").forEach(el => { el.textContent = Shopify.formatMoney(newPrice * 100); });
  }
  getPackKey(label, variants) {
    if (!label) return null;
    const match = label.match(/\d+/);
    const normalized = match ? match[0] : label.toLowerCase();
    if (variants[normalized]) return normalized;
    const lower = label.toLowerCase();
    const foundKey = Object.keys(variants).find(k => k.toLowerCase() === lower);
    return foundKey || Object.keys(variants)[0];
  }
}

class ClearCartHandler {
  async clear() {
    return await Cart.clear();
  }
}

// -------------------------------
// Sticky Trigger Handler (Mobile Only)
// -------------------------------
class StickyTriggerHandler {
  constructor(triggerSelector, sectionSelector, activeClass = "sticky-active", mobileMaxWidth = 1024) {
    this.triggerBtn = document.querySelector(triggerSelector);
    this.section = document.querySelector(sectionSelector);
    this.activeClass = activeClass;
    this.cart = null;
    this.mobileMaxWidth = mobileMaxWidth;
    if (!this.triggerBtn || !this.section || !this.isMobile()) return;

    eventBus.subscribe("cart:updated", (cart) => {
      this.cart = cart;
      this.handleCartUpdated();
    });
    window.addEventListener("resize", () => {
      if (!this.isMobile()) {
        this.triggerBtn.classList.remove(this.activeClass);
      } else {
        this.handleCartUpdated();
      }
    });
  }

  isMobile() {
    return window.innerWidth <= this.mobileMaxWidth;
  }

  handleCartUpdated() {
    if (!this.section || !this.triggerBtn || !this.cart || !this.isMobile()) return;

    const rect = this.section.getBoundingClientRect();
    const inView = rect.top < window.innerHeight && rect.bottom > 0;
    const hasItems = this.cart.item_count > 0;

    if (inView && hasItems) {
      this.triggerBtn.classList.add(this.activeClass);
    } else {
      this.triggerBtn.classList.remove(this.activeClass);
    }
  }
}


// -------------------------------
// Bootstrap
// -------------------------------
async function bootstrap() {
  await Cart.clear();
  new AddToCartFormHandler("product-form");
  new ProductTabHandler(".product-filter button", ".product-card-wrapper");
  new VariantAndPlanHandler(".pack-sizes button", ".custom-select", ".product-card-wrapper");
  new StickyTriggerHandler(
    ".cart-drawer-mobile .cdm-trigger", 
    ".products-container"
  );

  MiniCart.showSkeleton();
  const cart = await Cart.fetchCart();
  eventBus.publish("cart:updated", cart);
}

// -------------------------------
// EventBus Subscriptions
// -------------------------------
eventBus.subscribe("cart:updated", (cart) => {
  MiniCart.render(cart);
  PricingSummary.render(cart);
});

// -------------------------------
// Nutrition Modal
// -------------------------------
document.addEventListener("DOMContentLoaded", bootstrap);

(function() {
  const modal = document.getElementById('nutrition-modal');
  const img   = document.getElementById('nutrition-modal-img');
  const closeBtn = modal ? modal.querySelector('.nutrition-modal-close') : null;

  function openModal(src, title) {
    if (!modal || !img || !src) return;
    img.src = src;
    img.alt = title ? `Nutrition Facts for ${title}` : 'Nutrition Facts';
    modal.classList.add('show');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.style.overflow = 'hidden';
    closeBtn && closeBtn.focus();
  }

  function closeModal() {
    if (!modal) return;
    modal.classList.remove('show');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    if (img) img.removeAttribute('src');
  }

  document.addEventListener('click', function(e) {
    const trigger = e.target.closest('[data-nutrition-trigger]');
    if (trigger) {
      e.preventDefault();
      const src = trigger.getAttribute('data-img');
      const title = trigger.getAttribute('data-title');
      openModal(src, title);
      return;
    }
    if (e.target === modal || e.target.closest('.nutrition-modal-close')) {
      closeModal();
    }
  });

  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modal && modal.classList.contains('show')) {
      closeModal();
    }
  });
})();
